import { useState } from 'react';
import StatusBar from 'common/components/StatusBar';
import OfflineModeInfoModal from 'common/components/OfflineModeInfoModal';
import { ReactComponent as InfoIcon } from 'common/images/info-tooltip.svg';
import { useCloudReachability } from 'common/hooks';
import { buildCloudUrl } from 'common/utils/buildCloudUrl';
import { mobxInjectSelect } from 'common/utils';
import BEM from 'common/bem';

const classes = BEM.with('LocalServerStatusBar');

const cloudUrl = process.env.CLOUD_URL;

export const OFFLINE_MODE_BANNER_TEXT = 'Offline mode – Limited functionality';
export const OFFLINE_MODE_BANNER_INFO_LABEL = 'More information about offline mode';
export const RETURN_TO_CLOUD_LABEL = 'Return to Cloud';
export const RETURN_TO_CLOUD_UNAVAILABLE_LABEL = 'Return to Cloud — cloud not reachable yet';

type MobxProps = {
  networkStatus: string;
  isLocalServer: boolean;
  currentOrganizationId: string | null;
};

function LocalServerStatusBar({ networkStatus, isLocalServer, currentOrganizationId }: MobxProps) {
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  // Only probe while on a stand-in session with a configured cloud origin. `networkStatus`
  // gates the probe (no point probing while the LAN itself is down) but is not sufficient on
  // its own — an isolated facility LAN can be up while the cloud is unreachable.
  const cloudReachable = useCloudReachability(cloudUrl, {
    enabled: isLocalServer && networkStatus === 'ONLINE' && !!cloudUrl,
  });

  if (!isLocalServer) return null;

  const canReturnToCloud = cloudReachable && !!cloudUrl;

  const handleReturnToCloud = () => {
    if (!canReturnToCloud || !cloudUrl) return;
    window.location.href = buildCloudUrl(cloudUrl, currentOrganizationId);
  };

  return (
    <StatusBar variant={canReturnToCloud ? 'success' : 'error'} className={classes()}>
      <span className={classes('message')}>{OFFLINE_MODE_BANNER_TEXT}</span>
      <button
        type="button"
        className={classes('info-button')}
        aria-label={OFFLINE_MODE_BANNER_INFO_LABEL}
        data-test-id="offline-mode-banner-info"
        onClick={() => setIsInfoModalOpen(true)}
      >
        <InfoIcon className={classes('info-icon')} aria-hidden="true" />
      </button>
      {!!cloudUrl && (
        <button
          type="button"
          className={classes('button', { disabled: !canReturnToCloud })}
          // aria-disabled (not the native disabled attribute) keeps the button focusable and
          // in the accessibility tree so screen readers announce why it is unavailable.
          aria-disabled={!canReturnToCloud}
          aria-label={canReturnToCloud ? RETURN_TO_CLOUD_LABEL : RETURN_TO_CLOUD_UNAVAILABLE_LABEL}
          data-test-id="return-to-cloud"
          onClick={canReturnToCloud ? handleReturnToCloud : undefined}
        >
          {RETURN_TO_CLOUD_LABEL}
        </button>
      )}
      <OfflineModeInfoModal isOpen={isInfoModalOpen} onClose={() => setIsInfoModalOpen(false)} />
    </StatusBar>
  );
}

export default mobxInjectSelect<Record<string, never>, MobxProps>({
  networkStatusStore: ['networkStatus'],
  messengerStore: ['isLocalServer', 'currentOrganizationId'],
})(LocalServerStatusBar);
